import { Popover, type PopoverOrigin } from '@mui/material';
import { useSite } from '@on3/ui-lib/index';
import clsx from 'clsx';
import { Arrow } from 'components/Svg/Arrow';
import { PrimaryButton } from 'components/UI/Buttons/PrimaryButton';
import { TextLink } from 'components/UI/Link/TextLink';
import { useRouter } from 'next/router';
import { type MouseEventHandler } from 'react';
import { useCallback, useState } from 'react';

import styles from './LinkFilter.module.scss';

export interface ILinkFilterItem {
  key: number;
  name: string;
  pathname: string;
}
interface ILinkFilterProps {
  sportList: ILinkFilterItem[];
  selected: string;
  className?: string;
}
const anchorOrigin: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'center',
};

const transformOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'center',
};

/**Takes list: (key, name, pathname) and selected */
export const LinkFilter = ({
  sportList,
  selected,
  className,
}: ILinkFilterProps) => {
  const { currentSite } = useSite();
  const siteColor =
    (currentSite?.linkColor || currentSite?.primaryColor) ?? `#fe3b1f`;
  const router = useRouter();
  const { year, college, sport } = router.query;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      setAnchorEl(event.currentTarget);
    },
    [],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const getHref = useCallback(
    (pathname: string) => {
      return {
        pathname,
        query: {
          year,
          college,
          sport,
        },
      };
    },
    [college, sport, year],
  );

  return (
    <div className={clsx(styles.filterWrapper, className)}>
      <PrimaryButton
        aria-describedby={id}
        className={styles.button}
        fullWidth={false}
        onClick={handleClick}
        variant="text"
      >
        {selected}
        <Arrow className={styles.arrow} fill={siteColor} />
      </PrimaryButton>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={anchorOrigin}
        id={id}
        onClose={handleClose}
        open={open}
        transformOrigin={transformOrigin}
      >
        <div className={styles.listWrapper}>
          {sportList?.map((sportItem) => {
            return (
              <TextLink
                className={clsx(styles.link, {
                  [styles.active]: sportItem.name === selected.toLowerCase(),
                })}
                href={getHref(sportItem.pathname)}
                key={sportItem.key}
                underline="none"
                variant="subtitle1"
              >
                {sportItem.name}
              </TextLink>
            );
          })}
        </div>
      </Popover>
    </div>
  );
};
